import {
  HTMLProps,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { HiChevronDown } from 'react-icons/hi';
import cx from 'classnames';
import { useClickOutside } from '../../utils';
import Typography from '../Typography';
import Card from '../Card';
import styles from './Dropdown.module.scss';

interface DropdownProps extends Omit<HTMLProps<HTMLSelectElement>, 'children'> {
  options: {
    label: string;
    value: any;
  }[];
}

function Dropdown({ className, options, ...props }: DropdownProps, ref: any) {
  const trigger = useRef<HTMLSelectElement>(null);
  const [show, setShow] = useState(false);
  const rootRef = useClickOutside<HTMLDivElement>(() => {
    setShow(false);
  });

  useImperativeHandle(ref, () => trigger.current);

  const selectedOption = options.find(
    (option) => option.value.toString() === props.value?.toString(),
  );

  return (
    <div className={cx(className, styles.trigger)} ref={rootRef}>
      <Typography
        onClick={() => setShow(!show)}
        textColor={selectedOption ? 'text-1' : 'background-4'}
        className={styles.button}
        tabIndex={-1}
        textType="p2"
        as="button"
        type="button"
      >
        <span className={styles.display}>
          {selectedOption?.label ?? props.placeholder ?? 'Select an option'}
        </span>
        <span className={styles.iconFrame}>
          <HiChevronDown className={styles.icon} />
        </span>
      </Typography>
      <select
        {...props}
        value={props.value ?? ''}
        onFocus={(e) => {
          props.onFocus?.(e);
          setShow(false);
        }}
        className={styles.select}
        ref={trigger}
      >
        {options.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {show && (
        <Card as="ul" backgroundColor="background-1" className={styles.items}>
          {options.map((option) => (
            <li key={option.value}>
              <Typography
                onClick={() => {
                  if (!trigger.current) return;
                  trigger.current.value = option.value;
                  trigger.current.dispatchEvent(
                    new Event('change', { bubbles: true }),
                  );
                }}
                className={cx(
                  styles.item,
                  option.value === selectedOption?.value && styles.active,
                )}
                textType="p3"
                as="button"
                type="button"
              >
                {option.label}
              </Typography>
            </li>
          ))}
        </Card>
      )}
    </div>
  );
}

export default forwardRef(Dropdown);
